import { useMemo } from 'react';
import { CSVLink } from 'react-csv';
import useTranslation from 'next-translate/useTranslation';

// Lib
import { formatSocialProfileType } from '@/lib/socialProfile';
import { PENDO_CLASS_ANALYTICS_GROUP_OVERVIEW_EXPORT } from '@/lib/tools/pendo/class';

// Hooks
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';

// Utils
import useFormatKpiValue, {
    KPI_VALUE_MEDIA_VIEWER_CLASSIC
} from '@/utils/hooks/format/useFormatKpiValue';
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';

// Types
import type { GroupGlobalTableConfigType } from '@/lib/analyticsGroup/tables/groupGlobalTableConfig.type';
import type { ActionBarProps } from '@/components/pages/analyze-group/tables/GroupGlobalTable/ActionBar';
import type {
    DataGroupGlobalTableAnalyticsType,
    GroupGlobalTableAnalyticsFieldsType
} from '@/utils/hooks/swr/group/useGroupGlobalTableAnalytics/types';

// Components
import Button from '@/components/base/Button';

type ButtonExportTableProps = Pick<ActionBarProps, 'table' | 'periodSelected' | 'userDataFormat'>;

const ButtonExportTable = ({ table, userDataFormat, periodSelected }: ButtonExportTableProps) => {
    const { t } = useTranslation();
    const { activeGroup } = useActiveGroup();

    // Users data format
    const numbersFormat = userDataFormat.numbers_format;
    const dateFormat = userDataFormat.date_format;
    const timezone = userDataFormat.timezone;

    const { formatDateTime } = useFormatDateTime(timezone);
    const { formatKpiValue } = useFormatKpiValue(KPI_VALUE_MEDIA_VIEWER_CLASSIC, numbersFormat);

    const rowsSorted = table.getSortedRowModel().rows;
    const visibleColumnsOrdered = table.getVisibleLeafColumns();

    const exportDataFormatted = useMemo(() => {
        const headers = visibleColumnsOrdered.map((column) => {
            const header = column.columnDef.header as () => {
                props: { i18nKey: string };
            };

            const columnId = column.id as unknown as keyof RequiredFor<
                GroupGlobalTableConfigType['columnsVisibility'],
                'avatar'
            >;

            if (columnId === 'avatar') {
                return {
                    label: t(
                        'analytics:table_comparative_group.export_csv_column_group_social_profiles'
                    ),
                    key: `group_social_profile`
                } as const;
            }

            return { label: t(header().props.i18nKey), key: `${columnId}` } as const;
        });

        const data = rowsSorted.map((row) => {
            const cells = row.getVisibleCells();

            return cells.reduce(
                (acc, cell) => {
                    if (cell.column.id === 'avatar') {
                        const cellAvatarValue = cell.getValue() as
                            | DataGroupGlobalTableAnalyticsType[number]['group']
                            | DataGroupGlobalTableAnalyticsType[number]['social_profile'];

                        return {
                            ...acc,
                            group_social_profile:
                                'social_network_type' in cellAvatarValue
                                    ? `${cellAvatarValue.slug} (${formatSocialProfileType(
                                          cellAvatarValue
                                      )})`
                                    : `${cellAvatarValue.name} (group)`
                        };
                    }

                    // Format KPI value
                    const cellKpiValue =
                        cell.getValue() as DataGroupGlobalTableAnalyticsType[number][GroupGlobalTableAnalyticsFieldsType];
                    return {
                        ...acc,
                        [cell.column.id]:
                            cellKpiValue &&
                            'value' in cellKpiValue &&
                            typeof cellKpiValue.value === 'number'
                                ? (formatKpiValue(
                                      cellKpiValue.value,
                                      cell.column.columnDef?.meta?.kpi_format ??
                                          KPI_VALUE_MEDIA_VIEWER_CLASSIC
                                  ) as string)
                                : 'N/A'
                    };
                },
                {} as Record<(typeof headers)[number]['key'], string>
            );
        });

        return { headers, data };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(rowsSorted), JSON.stringify(visibleColumnsOrdered)]);

    if (!rowsSorted) return null;

    const { headers, data } = exportDataFormatted;

    const startDateFormatted = formatDateTime(periodSelected.start, dateFormat);
    const endDateFormatted = formatDateTime(periodSelected.end, dateFormat);

    return (
        <Button
            as={CSVLink}
            data={data}
            icon="reports"
            size="sm"
            headers={headers}
            filename={`${activeGroup.name}-${startDateFormatted}-${endDateFormatted}`}
            className={PENDO_CLASS_ANALYTICS_GROUP_OVERVIEW_EXPORT}>
            <span>{t('analytics:export_table')}</span>
        </Button>
    );
};

export default ButtonExportTable;
